import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { all } from 'ramda';
import { isItemCuttable } from '../../media-manager-sdk/items';
import { MediaManagerSDKAnyItem, MediaManagerSDKFolder } from '../../media-manager-sdk';

interface IClipboardContext {
  cutItems: MediaManagerSDKAnyItem[];
  copiedItems: MediaManagerSDKAnyItem[];
  setCopiedItems: (items: MediaManagerSDKAnyItem[], sourceFilesFolderPath: MediaManagerSDKFolder[] | null) => void;
  setCutItems: (items: MediaManagerSDKAnyItem[], sourceFilesFolderPath: MediaManagerSDKFolder[] | null) => void;
  sourceFilesFolderPath: MediaManagerSDKFolder[] | null;
}

const ClipboardContext = React.createContext<IClipboardContext>(undefined);

export const ClipboardProvider: FC = ({ children }) => {
  const [cutItems, _setCutItems] = useState<MediaManagerSDKAnyItem[]>([]);
  const [copiedItems, _setCopiedItems] = useState<MediaManagerSDKAnyItem[]>([]);
  const [sourceFilesFolderPath, setSourceFilesFolderPath] = useState<MediaManagerSDKFolder[] | null>(null);

  const setCutItems = useCallback(
    (newCutItems: MediaManagerSDKAnyItem[], newSourceFilesFolderPath: MediaManagerSDKFolder[] = null) => {
      if (!all(isItemCuttable, newCutItems)) {
        return;
      }
      _setCutItems(newCutItems);
      _setCopiedItems([]);
      setSourceFilesFolderPath(newSourceFilesFolderPath);
    },
    [],
  );

  const setCopiedItems = useCallback(
    (newCopiedItems: MediaManagerSDKAnyItem[], newSourceFilesFolderPath: MediaManagerSDKFolder[] = null) => {
      if (!all(isItemCuttable, newCopiedItems)) {
        return;
      }
      _setCutItems([]);
      _setCopiedItems(newCopiedItems);
      setSourceFilesFolderPath(newSourceFilesFolderPath);
    },
    [],
  );

  return (
    <ClipboardContext.Provider
      value={useMemo(
        () => ({
          cutItems,
          copiedItems,
          setCutItems,
          setCopiedItems,
          sourceFilesFolderPath,
        }),
        [copiedItems, cutItems, setCopiedItems, setCutItems, sourceFilesFolderPath],
      )}
    >
      {children}
    </ClipboardContext.Provider>
  );
};

export const useClipboard = () => {
  const value = useContext(ClipboardContext);

  if (!value) {
    throw new Error('no ClipboardContext.Provider');
  }

  return value;
};
